import { Alert, Button, Form, Modal, Badge, Row, Col } from "react-bootstrap"
import { useAppSelector } from "../../redux/hooks";
import { useDispatch } from "react-redux";
import { ThirdPartyDispatch } from "../ThirdPartyList/ThirdPartyFuncs";
import { useContext, useEffect, useRef, useState } from "react";
import AnalyzerFuncs from "./analyzer";
import { lddStateObject, setRenAIMortgageSuccess } from "../../redux/reducer";
import { RPWDataReader, getDataReader } from "../../livedataFuncs";
import { Import } from "./Import";
import fieldDef from "./FieldDef";
import "./DocsDropbox.css";
import ContactHelp from "./ContactHelp";
import { LoadingIcon } from "../Loading";
import { NavObjectType } from "../navigationTypes";
import { NavigationContext } from "../../Context/NavigationContext";
import {pdfjs} from 'react-pdf'; 
import { DocumentDisplay } from "./DocumentDisplay";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type IProps = {
    fileLocation: string;
    responseData?: Record<string, any>;
    base64Content?: string;
    uploadDoc?: string;
}

export enum StepsEnum {
    BeforeUpload = 1,
    Uploading = 2,
    UploadSuccess = 3,
    UploadFail = 4,
    ContactHelp = 5,
    SendingEmail = 6,
    ProcessingFail = 7,
    DisplayPDF = 8,
}

export const DocsDropbox = (props: IProps) => {
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    const SESSION_INFO = useAppSelector((state: lddStateObject) => state.SessionInfo);
    const dispatch = useDispatch();
    let DataReaderObj: RPWDataReader;
    let AppID: string;
    const AIName = "RenAI";
    if (livedataRootContainer.dataRoot) {
        DataReaderObj = getDataReader(livedataRootContainer.dataRoot);
        AppID = DataReaderObj.getAppId();
    }

    // cur state & ref
    const FormControlRef = useRef(null);
    const FormSubmitRef = useRef(null);
    const ImportRef = useRef<HTMLButtonElement>(null);
    const ContactRef = useRef<HTMLButtonElement>(null);
    const [step, setStep] = useState<StepsEnum>(props?.responseData ? StepsEnum.UploadSuccess : StepsEnum.BeforeUpload);
    const [uploadDoc, setUploadDoc] = useState<File>(null);
    const [validated, setValidated] = useState<boolean>(false);
    const [validatedToProcess, setValidatedToProcess] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>(null);
    const [analyzedRes, setAnalyzedRes] = useState(props?.responseData || {});
    const [numOfField, setNumOfField] = useState(0);
    const [validFeedback, setValidFeedback] = useState(false);
    const [docID, setDocID] = useState("");
    const [lastStep, setLastStep] = useState<StepsEnum>(null);
    const [visible, setVisible] = useState<boolean>(true);
    const [showPDF, setShowPDF] = useState<boolean>(false);
    const [base64Data, setBase64Data] = useState<string | null>(null);
    const [hasValidLender, setHasValidLender] = useState<boolean>(false);
    const nav = useContext(NavigationContext);
    const fileParams: NavObjectType = {
        component: 'main',
        url: 'lfmaster.asp',
        params: {
            'formasp': 'purfileedit',
            'tb': 'TPAppXML',
            'id': AppID,
            'compat': '1',
            'ReactDocument': '-1'
        },
        controller: {
            tabName: "Basic",
            tabNumber: 0,
            refresh: false,
        },
    };

    useEffect(() => {
        const isAnalyzerSuccess = ((step === StepsEnum.UploadSuccess || step === StepsEnum.DisplayPDF) || props?.responseData) && analyzedRes && analyzedRes.Lender;
        if (isAnalyzerSuccess) {
            dispatch(setRenAIMortgageSuccess(true));
        }
    }, [step, props?.responseData, analyzedRes, dispatch]);

    // Initialize numOfField when responseData is provided
    useEffect(() => {
        if (props.responseData) {
            successCallback(props.responseData);
            setVisible(true);
        }
    }, [props.responseData]);

    // hooks
    useEffect(() => {
        if (uploadDoc) {
            // send to analyzer automatically
            submitHandler();
        }
    }, [uploadDoc])

    useEffect(() => {
        
        const fetchAnalyzerResponse = async () => {
          try {
            if (props.fileLocation && props.fileLocation !== '' && props.fileLocation !== '--ldd-default-renai-request' && visible) {
              await AnalyzerFuncs.getAnalyzerResponse(SESSION_INFO, props.fileLocation, successCallback);
            }
          } catch (err) {
            console.error('Failed to fetch analyzer response:', err);
          }
        };
    
        fetchAnalyzerResponse();
    
    }, [props.fileLocation, visible]);

    // handlers
    const checkFileSize = (file): boolean => {
        const MAX_FILE_SIZE = 10 * Math.pow(2, 20); // 10 MB
        if (file.size > MAX_FILE_SIZE) {
            setErrorMsg(`This file exceeds the upload limit of ${MAX_FILE_SIZE}MB. Please try another file.`);
            setValidated(false);
            return false;
        }
        return true;
    }
    const successCallback = async (data) => {
        // console.log('success callback')
        setAnalyzedRes(data);
        const base64data = await getBase64Data(); 
        setBase64Data(base64data);
        setShowPDF(true);
        setStep(StepsEnum.DisplayPDF);
        let preSelectedField = 0;
        preSelectedField += fieldDef.Lender.filter(e => e.attribute && data[e.attribute]).length;
        preSelectedField += fieldDef.MortgageInfo.filter(e => e.attribute && data[e.attribute]).length;
        preSelectedField += fieldDef.ChargeProv.filter(e => e.attribute && data[e.attribute]).length;
        setNumOfField(preSelectedField);
    }
    const failedCallback = (errorCode?: string) => {
        // console.log('failure callback');
        setUploadDoc(null);
        if (FormControlRef.current) {
            FormControlRef.current.value = "";
        }
        if (!errorCode) {
            setStep(StepsEnum.UploadFail);
        } else {
            setStep(StepsEnum.ProcessingFail);
        }
    }
    const submitHandler = (e?) => {
        // console.log("uploadNewDocPopSubmit")
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // upload
        setStep(StepsEnum.Uploading);
        AnalyzerFuncs.upload(uploadDoc, SESSION_INFO, AppID, setDocID, successCallback, failedCallback);
    }

    const changeHandler = (e) => {
        // console.log("uploadNewDocPopChange")
        setStep(StepsEnum.BeforeUpload);    // reset step
        e.preventDefault();
        e.stopPropagation();
        setValidatedToProcess(true);
        // validation
        if (!checkFileSize(uploadDoc ? uploadDoc : ((e.target) as any).files[0])) {
            setValidated(false);
            setValidatedToProcess(false);
            return;
        }
    }

    const selectOneField = (select: boolean) => {
        if (select) {
            setNumOfField(numOfField + 1);
        } else {
            setNumOfField(numOfField - 1);
        }
    }
    const cancelHandler = () => {
        if (step !== StepsEnum.ContactHelp) {
            ThirdPartyDispatch(dispatch, "MortgageDropbox", false, { 'fileLocation': props.fileLocation });
            setVisible(false);
            // Navigate if fileLocation is valid
            if (props.fileLocation && props.fileLocation !== '' && props.fileLocation !== '--ldd-default-renai-request') {
                nav(fileParams);
            }
        } else {
            setStep(lastStep);
        }
    }
    const shouldShowModal = visible && (
        props.fileLocation === '--ldd-default-renai-request' || 
        (props.fileLocation && props.fileLocation.trim() !== '')
    );
    
    const getBase64Data = async (): Promise<string> => {   
        if (!uploadDoc || !(uploadDoc instanceof Blob)) {
            return null;
        }

        return URL.createObjectURL(uploadDoc);
        // const result = await uploadDoc.arrayBuffer();
        // const uint8array = new Uint8Array(result);
        // const charArray = Array.from(uint8array, byte=> String.fromCharCode(byte));
        // const stringFromUint8 = charArray.join('');
        // const encodedString = encodeURIComponent(stringFromUint8);
        // return encodedString
    };

    const closeViewer = () => {
        setShowPDF(false);
        setStep(StepsEnum.UploadSuccess);
    }

    const fileUploadClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        FormControlRef.current?.click()
    }

    const handleDragEvent = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            setUploadDoc(file);
        }
    };

    return (
        <Modal show={shouldShowModal} size="xl" className={`ai-analyzer ${showPDF ? 'pdf-mode':''}`} enforceFocus={false} dialogClassName={showPDF? 'display-pdf': ""} centered>
            <div className="modal-content-inner">
                <Modal.Header>
                    <h2><i className="renai-icon renai-pill"></i> Import</h2>
                    {((uploadDoc?.type === "application/pdf" && step === StepsEnum.UploadSuccess) || (props?.base64Content && step !== StepsEnum.DisplayPDF)) && 
                    <Button onClick={() => {
                        setShowPDF(true);
                        setStep(StepsEnum.DisplayPDF);
                    }}><i className="fa-solid fa-file-pdf"></i>Open PDF Viewer</Button>}
                    {((uploadDoc?.type === "application/pdf" || props?.base64Content) && step === StepsEnum.DisplayPDF) && 
                    <Button onClick={() => {
                        setShowPDF(false);
                        setStep(StepsEnum.UploadSuccess);
                    }}><i className="fa-solid fa-file-pdf"></i>Close PDF Viewer</Button>}
                </Modal.Header>

                <Modal.Body>
                    {[StepsEnum.BeforeUpload, StepsEnum.UploadFail, StepsEnum.ProcessingFail].includes(step) &&
                        <Form noValidate validated={validated}
                            autoComplete="off"
                            onSubmit={submitHandler}
                            onChange={changeHandler}
                            className="form-container">
                            
                            <Alert><b>Import Mortgage Instructions with {AIName}.</b> We've improved {AIName} to successfully process the vast majority of mortgage instructions and commitments you upload! In the rare event that you encounter a problem, please reach out to our support team. For Toronto-Dominion Bank (TD), download <a target="_blank" href="pdf/LDD-Sub-Acknldgmnt-TD.pdf">this pdf</a> and send the completed version to <b>support@ldd.ca</b>.</Alert>

                            <Form.Group controlId="docUploader" style={{height: '100%'}}>
                                <Form.Control type="file" ref={FormControlRef} accept="application/pdf" style={{display: "none"}}
                                    onChange={(e) => {
                                        setErrorMsg(null);
                                        if (!(e.target as any).files || !(e.target as any).files.length || ((e.target) as any).files[0].type !== "application/pdf") {
                                            // dont allow folder
                                            setErrorMsg("Please upload a pdf of your Mortgage Instructions document.");
                                            e.target.value = "";
                                            e.preventDefault();
                                            e.stopPropagation();
                                        } else { setUploadDoc(((e.target) as any).files[0]) }
                                    }} 
                                />
                                <div className="file-upload" onClick={fileUploadClick} onDragEnter={handleDragEvent} onDragLeave={handleDragEvent} onDragOver={handleDragEvent} onDrop={handleDrop}>
                                    <div className="file-upload-inner">
                                        {(errorMsg || step === StepsEnum.UploadFail || step === StepsEnum.ProcessingFail) ?
                                            <>
                                                <i className="fa-duotone fa-cloud-exclamation status-fail"></i>
                                                <p>
                                                    {step === StepsEnum.UploadFail ?
                                                        <b>There was an error in uploading the Mortgage Instructions document.</b>
                                                        :
                                                        <>
                                                            <b>{step === StepsEnum.ProcessingFail ? "This lender is not currently supported by RenAI." : "RenAI could not process your document."}</b>
                                                            <br />
                                                            {step === StepsEnum.ProcessingFail ?
                                                                <>
                                                                    Upload mortgage instructions from a supported lender
                                                                    <br />
                                                                    or request support for this lender by submitting a feedback below.
                                                                </>
                                                                :
                                                                "Please try again with a different document or contact support."
                                                            }
                                                        </>
                                                    }
                                                </p>
                                            </>
                                            :
                                            <>
                                                <i className="fa-duotone fa-cloud-arrow-up status-info"></i>
                                                <p>
                                                    {(uploadDoc && validatedToProcess) ?
                                                        <b>{uploadDoc.name}</b>
                                                        :
                                                        <b>Drag and drop a pdf document here to analyze</b>
                                                    }
                                                    <br />
                                                    or
                                                </p>
                                            </>
                                        }

                                        <Button type="button" variant="primary" onClick={fileUploadClick}>
                                            {(errorMsg || step === StepsEnum.UploadFail || step === StepsEnum.ProcessingFail) ? "Try Again" : "Select File"}
                                        </Button>

                                        <Form.Control.Feedback type="invalid">Please select file to upload </Form.Control.Feedback>
                                    </div>
                                </div>
                            </Form.Group>

                            <Button type="submit" ref={FormSubmitRef} style={{ display: "none" }}></Button>
                        </Form>
                    }
                    {step === StepsEnum.Uploading &&
                        <LoadingIcon showMsg={true} extraMsg={AIName + " is now analyzing your document"} inline={true} />
                    }
                    {((step === StepsEnum.UploadSuccess || step === StepsEnum.DisplayPDF) || props?.responseData) && analyzedRes && analyzedRes.Lender && step !== StepsEnum.ContactHelp &&
                        <Import data={analyzedRes} selectOneField={selectOneField} fileLocation={props.fileLocation} ref={ImportRef} onLenderChange={setHasValidLender} /> 
                    }
                    {step === StepsEnum.ContactHelp &&
                        <ContactHelp docID={docID} fromStep={lastStep} setStep={setStep} setValidFeedback={setValidFeedback} ref={ContactRef} />
                    }
                    {step === StepsEnum.SendingEmail &&
                        <LoadingIcon showMsg={true} extraMsg={"Sending Email"} inline={true} />
                    }
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="outline-primary" onClick={cancelHandler}>Cancel</Button>
                    <div>
                        {/* {step === StepsEnum.BeforeUpload && (
                            <Button
                                variant={(uploadDoc && validatedToProcess) ? "primary" : "secondary"}
                                onClick={(uploadDoc && validatedToProcess) ? () => {
                                    if (FormSubmitRef.current) {
                                        FormSubmitRef.current.click()
                                    }
                                } : () => { }}>Upload</Button>
                        )} */}
                        {([StepsEnum.UploadFail, StepsEnum.UploadSuccess, StepsEnum.ProcessingFail, StepsEnum.DisplayPDF].includes(step)) &&
                            <Button variant="warning" onClick={() => { setLastStep(step); setStep(StepsEnum.ContactHelp); }}>
                                Need help?
                            </Button>
                        }
                        {([StepsEnum.UploadSuccess, StepsEnum.DisplayPDF].includes(step)) && (
                            <Button
                                variant={numOfField > 0 && hasValidLender ? "primary" : "secondary"}
                                disabled={numOfField === 0 || !hasValidLender}
                                onClick={numOfField > 0 && hasValidLender ? () => {
                                    if (ImportRef.current) {
                                        ImportRef.current.click();
                                        ThirdPartyDispatch(dispatch, "MortgageDropbox", false);
                                        setVisible(false);
                                        if (props.fileLocation && props.fileLocation !== '' && props.fileLocation !== '--ldd-default-renai-request') {
                                            nav(fileParams);
                                        }
                                    }
                                } : undefined}>{`Import Selected To File (${numOfField})`}</Button>
                        )}
                        {step === StepsEnum.ContactHelp &&
                            <Button
                                variant={validFeedback ? "primary" : "secondary"}
                                onClick={validFeedback ? () => {
                                    if (ContactRef.current) {
                                        ContactRef.current.click()
                                    }
                                } : undefined}>Send Feedback</Button>
                        }
                    </div>
                </Modal.Footer>
            </div>

            {step === StepsEnum.DisplayPDF &&
                <DocumentDisplay base64Data={props?.base64Content || base64Data} closeViewer={closeViewer} fileName={props?.uploadDoc || uploadDoc?.name}/>
            }
        </Modal>
    )

}